/**
 * What a right-click on a conversation in the list offers.
 *
 * Triaging a morning's mail is the one job on this desk that is done from the
 * list rather than from the reading pane, and until now every verb in it -
 * archive, spam, star, label, delete - lived in the pane's header. That meant
 * opening a conversation to file it, which also marks it read, so a thread you
 * archived unseen came back marked as though somebody had read it.
 *
 * Read state is therefore the entry that matters most here: it is the only way
 * in the app to mark a thread unread again.
 */
import {
  Archive,
  ArchiveRestore,
  Copy,
  Filter,
  Mail,
  MailOpen,
  Reply,
  ShieldAlert,
  ShieldCheck,
  Star,
  Trash2,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { MailThread } from "../../../lib/mail";
import type { CrmLabel } from "../../../lib/crmLabels";
import { LabelItems, useCopyToast } from "../../ui";
import { threadWho } from "./who";

/** Who the thread is with, by the best name available - the same order the list
 *  row uses, so the menu's heading matches the line it opened from. The rule
 *  itself moved to `who.ts` when the folded list view needed it without a React
 *  import; the door stays here so no caller had to change its import. */
export { threadWho };

export function ThreadRowItems({
  thread,
  labels,
  close,
  onOpen,
  onReply,
  onSearch,
  onPatch,
  onDelete,
}: {
  thread: MailThread;
  labels: CrmLabel[];
  close: () => void;
  onOpen: (id: string) => void;
  onReply: (thread: MailThread) => void;
  /** Put a term in the list's search box - "everything from this sender". */
  onSearch: (term: string) => void;
  onPatch: (id: string, body: Record<string, unknown>) => Promise<void>;
  onDelete: (thread: MailThread) => void;
}) {
  const t = useT();
  const copy = useCopyToast();
  const spam = thread.status === "spam";
  const archived = thread.status === "archived";
  const sender = thread.messages?.[0]?.fromAddress ?? null;

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{threadWho(thread)}</div>
        <div className="crm-menu-s">{thread.subject || "-"}</div>
      </div>

      <button type="button" className="crm-mi" onClick={() => act(() => onOpen(thread.id))}>
        <Mail className="size-4" />
        {t("email.row.open")}
      </button>

      <button type="button" className="crm-mi" onClick={() => act(() => onReply(thread))}>
        <Reply className="size-4" />
        {t("email.row.reply")}
      </button>

      {/* Marking read from here does NOT open the thread, which is the whole
          point: the badge clears without the conversation being counted as
          read by anyone else looking at the shared mailbox. */}
      <button type="button" className="crm-mi" onClick={() => act(() => void onPatch(thread.id, { unread: !thread.unread }))}>
        {thread.unread ? <MailOpen className="size-4" /> : <Mail className="size-4" />}
        {t(thread.unread ? "email.row.markRead" : "email.row.markUnread")}
      </button>

      <button
        type="button"
        className={cn("crm-mi", thread.starred && "is-on")}
        onClick={() => act(() => void onPatch(thread.id, { starred: !thread.starred }))}
      >
        <Star className={cn("size-4", thread.starred && "fill-amber-400 text-amber-400")} />
        {t(thread.starred ? "email.row.unstar" : "email.row.star")}
      </button>

      <button
        type="button"
        className="crm-mi"
        onClick={() => act(() => void onPatch(thread.id, { status: archived ? "open" : "archived" }))}
      >
        {archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
        {t(archived ? "email.unarchive" : "email.archive")}
      </button>

      <button
        type="button"
        className="crm-mi"
        onClick={() => act(() => void onPatch(thread.id, { status: spam ? "open" : "spam" }))}
      >
        {spam ? <ShieldCheck className="size-4" /> : <ShieldAlert className="size-4" />}
        {t(spam ? "email.notSpam" : "email.markSpam")}
      </button>

      {sender ? (
        <>
          <button type="button" className="crm-mi" onClick={() => act(() => copy(sender))}>
            <Copy className="size-4" />
            {t("email.row.copyAddress")}
          </button>
          <button type="button" className="crm-mi" onClick={() => act(() => onSearch(sender))}>
            <Filter className="size-4" />
            {t("email.row.findSender")}
          </button>
        </>
      ) : null}

      {/* The menu stays open through a label click: labelling is the one action
          here an operator does more than once in a row. */}
      <LabelItems
        title={t("mail.labels")}
        labels={labels}
        tags={thread.tags ?? []}
        onApply={(next) => onPatch(thread.id, { tags: next })}
      />

      <div className="crm-menu-sep" />
      <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(() => onDelete(thread))}>
        <Trash2 className="size-4" />
        {t("email.delete")}
      </button>
    </>
  );
}
